// AJ-EP (MG-ILE-918) · tarea 918-2. La tarjeta «Parámetros del sistema» de Administración › Reglas
// de negocio: los parámetros que hasta hoy solo se cambiaban por API (AT S3 §5.2).
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-918.md`. La lista llega del servidor con
// metadatos (`GET /administracion/parametros`): etiqueta, valor ya escrito para leer, estado
// declarado y tipo. La pantalla NO conoce los parámetros de antemano ni concatena valor y unidad
// (§8.1 R1). Cambiar exige motivo (C3) y la validación del valor es la del catálogo del servidor:
// su 422 se pinta bajo el campo (C2). Tras guardar se vuelven a pedir la lista y el historial
// (§8.1 R3). El historial muestra la línea que arma el servidor (`texto`, C3).
//
// Vacíos de la spec que esta tarjeta resuelve con una propuesta anotada: nombre de la tarjeta (1),
// control por tipo (2: número para `entero`, una línea por valor para `lista_de_textos`, un número
// por campo para `topes_por_campo`, dos horas «Desde» y «Hasta» para `horario`), historial en un panel bajo la tabla (3) y textos de vacío y
// éxito (6).
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { History, Pencil, RotateCcw, SlidersHorizontal } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Mono, Modal, StatusBadge, Textarea,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import { cambiarParametro, historialDeParametro, listarParametros } from '../api/reglas';
import type { ParametroEditable, VersionDeParametro } from '../api/reglas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { puedeDescartar } from '../lib/descarte';

/** Texto vigente de la misma pantalla (HU-31) y de `MOTIVO_OBLIGATORIO` del servidor. */
const MOTIVO_OBLIGATORIO = 'El motivo es obligatorio: es lo que explica en una inspección por qué cambió la regla.';

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'etiqueta', label: 'Parámetro', className: 'w-[40%]' },
  { key: 'valor', label: 'Valor vigente', className: 'w-[20%]' },
  { key: 'estado', label: 'Estado', className: 'w-[25%]' },
  { key: 'acciones', label: '' },
];

/** Los topes por campo son una rejilla: el cuadro ancho. El resto cabe en el estándar. */
const ANCHO_POR_TIPO: Record<string, string> = { topes_por_campo: 'xl' };

/** El valor del formulario tal como se edita, por tipo. */
type Borrador = { texto: string; topes: Record<string, string>; inicio: string; fin: string };

const VACIO: Borrador = { texto: '', topes: {}, inicio: '', fin: '' };

const borradorDe = (p: ParametroEditable): Borrador => {
  if (p.tipo === 'lista_de_textos') {
    return { ...VACIO, texto: Array.isArray(p.valor_vigente) ? (p.valor_vigente as unknown[]).map(String).join('\n') : '' };
  }
  if (p.tipo === 'topes_por_campo' && p.valor_vigente && typeof p.valor_vigente === 'object') {
    return { ...VACIO, topes: Object.fromEntries(Object.entries(p.valor_vigente as Record<string, unknown>).map(([k, v]) => [k, String(v)])) };
  }
  if (p.tipo === 'horario') {
    // Nace sin valor: los dos campos vacíos, sin una hora sugerida que nadie decidió.
    const h = (p.valor_vigente ?? {}) as { inicio?: unknown; fin?: unknown };
    return { ...VACIO, inicio: typeof h.inicio === 'string' ? h.inicio : '', fin: typeof h.fin === 'string' ? h.fin : '' };
  }
  return { ...VACIO, texto: p.valor_vigente === null || p.valor_vigente === undefined ? '' : String(p.valor_vigente) };
};

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * La validación del horario con el MISMO texto que `ParametroEditable._validar_horario` del
 * servidor. Se adelanta en pantalla para no esperar el 422; el servidor la repite igual.
 */
const errorDelHorario = (etiqueta: string, inicio: string, fin: string): string | null => {
  if (!HORA.test(inicio) || !HORA.test(fin)) return `${etiqueta}: indica la hora de inicio y la de fin como hh:mm.`;
  if (fin <= inicio) return `${etiqueta}: la hora de fin tiene que ser posterior a la de inicio.`;
  return null;
};

/** Lo que viaja en el PUT. Un número que no lo es se manda tal cual: el 422 del servidor lo dice. */
const valorDe = (p: ParametroEditable, b: Borrador): unknown => {
  const numero = (texto: string): unknown => (/^\d+$/.test(texto.trim()) ? Number(texto.trim()) : texto.trim());
  if (p.tipo === 'lista_de_textos') return b.texto.split('\n').map((l) => l.trim()).filter(Boolean);
  if (p.tipo === 'topes_por_campo') return Object.fromEntries(Object.entries(b.topes).map(([k, v]) => [k, numero(v)]));
  if (p.tipo === 'horario') return { inicio: b.inicio, fin: b.fin };
  return numero(b.texto);
};

function ModalCambiarParametro({ parametro, onCerrar, onGuardado }: {
  parametro: ParametroEditable | null;
  onCerrar: () => void;
  onGuardado: (p: ParametroEditable) => void;
}) {
  const [borrador, setBorrador] = useState<Borrador>(VACIO);
  const [motivo, setMotivo] = useState('');
  const [errorDelValor, setErrorDelValor] = useState<string | null>(null);
  const [errorDelMotivo, setErrorDelMotivo] = useState<string | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const inicial = useRef('');

  useEffect(() => {
    if (!parametro) return;
    const b = borradorDe(parametro);
    setBorrador(b);
    inicial.current = JSON.stringify(b);
    setMotivo('');
    setErrorDelValor(null);
    setErrorDelMotivo(null);
    setFallo(null);
  }, [parametro]);

  const sucio = JSON.stringify(borrador) !== inicial.current || Boolean(motivo);

  const guardar = async () => {
    if (!parametro) return;
    setFallo(null);
    // El horario se valida aquí con el texto del servidor; los demás tipos, con su 422.
    const errorLocal = parametro.tipo === 'horario' ? errorDelHorario(parametro.etiqueta, borrador.inicio, borrador.fin) : null;
    setErrorDelValor(errorLocal);
    // C3: sin motivo no se llama al servidor.
    const sinMotivo = !motivo.trim();
    setErrorDelMotivo(sinMotivo ? MOTIVO_OBLIGATORIO : null);
    if (errorLocal || sinMotivo) return;
    setGuardando(true);
    try {
      await cambiarParametro(parametro.clave, valorDe(parametro, borrador), motivo.trim());
      onGuardado(parametro);
    } catch (e) {
      const f = aFallo(e);
      if (f.estado === 422) setErrorDelValor(f.message);
      else setFallo(f);
    } finally {
      setGuardando(false);
    }
  };

  let control: ReactNode = null;
  if (parametro?.tipo === 'lista_de_textos') {
    control = (
      <Field label="Nuevo valor" required hint="Un valor por línea." error={errorDelValor ?? undefined}>
        <Textarea data-testid="reglas-parametro-form-valor" rows={6} value={borrador.texto}
          onChange={(e) => setBorrador({ ...borrador, texto: e.target.value })} />
      </Field>
    );
  } else if (parametro?.tipo === 'topes_por_campo') {
    control = (
      <Field label="Nuevo valor" grupo required hint="El tope de cada campo del formulario." error={errorDelValor ?? undefined}
        data-testid="reglas-parametro-form-valor">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.keys(borrador.topes).map((campo) => (
            <Field key={campo} label={<Mono className="normal-case">{campo}</Mono>}>
              <Input type="number" min={1} step={1} inputMode="numeric" value={borrador.topes[campo]}
                onChange={(e) => setBorrador({ ...borrador, topes: { ...borrador.topes, [campo]: e.target.value } })} />
            </Field>
          ))}
        </div>
      </Field>
    );
  } else if (parametro?.tipo === 'horario') {
    control = (
      <Field label="Nuevo valor" grupo required hint="De lunes a viernes, en la hora de cada país y sin sus feriados." error={errorDelValor ?? undefined}
        data-testid="reglas-parametro-form-valor">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Desde" required>
            <Input data-testid="reglas-parametro-form-valor-inicio" type="time" step={60} value={borrador.inicio}
              onChange={(e) => { setBorrador({ ...borrador, inicio: e.target.value }); setErrorDelValor(null); }} />
          </Field>
          <Field label="Hasta" required>
            <Input data-testid="reglas-parametro-form-valor-fin" type="time" step={60} value={borrador.fin}
              onChange={(e) => { setBorrador({ ...borrador, fin: e.target.value }); setErrorDelValor(null); }} />
          </Field>
        </div>
      </Field>
    );
  } else if (parametro) {
    control = (
      <Field label="Nuevo valor" required hint={parametro.unidad ? `En ${parametro.unidad}.` : undefined} error={errorDelValor ?? undefined}>
        <Input data-testid="reglas-parametro-form-valor" type="number" min={1} step={1} inputMode="numeric" value={borrador.texto}
          onChange={(e) => setBorrador({ ...borrador, texto: e.target.value })} />
      </Field>
    );
  }

  return (
    <Modal open={parametro !== null} onClose={onCerrar} sucio={sucio} data-testid="reglas-parametro-form"
      eyebrow="Administración · Reglas de negocio" title={`Cambiar ${parametro?.etiqueta ?? ''}`} icon={Pencil}
      ancho={ANCHO_POR_TIPO[parametro?.tipo ?? ''] ?? 'lg'}
      footer={(
        <>
          <Button variant="secondary" onClick={() => { if (puedeDescartar(sucio)) onCerrar(); }} data-testid="reglas-parametro-form-cancelar">Cancelar</Button>
          <Button onClick={() => { guardar(); }} disabled={guardando} data-testid="reglas-parametro-form-guardar">
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      )}>
      <div className="space-y-4">
        {fallo && <Alert tone="danger" title="No se pudo guardar" data-testid="reglas-parametro-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
        <div>
          <p className="block font-semibold text-meta text-ink-soft mb-1">Valor actual</p>
          <p data-testid="reglas-parametro-form-actual" className="text-body text-ink">{parametro?.texto_del_valor}</p>
        </div>
        {control}
        <Field label="Motivo del cambio" required error={errorDelMotivo ?? undefined}
          hint="Queda en el historial y en la auditoría, con quién y cuándo" contador={`${motivo.length} / 1000`}>
          <Textarea data-testid="reglas-parametro-form-motivo" rows={2} maxLength={1000} value={motivo}
            onChange={(e) => { setMotivo(e.target.value); setErrorDelMotivo(null); }} />
        </Field>
      </div>
    </Modal>
  );
}

export default function ParametrosDelSistema({ onExito }: {
  /** El aviso de éxito es el de la pantalla de reglas (`reglas-exito`), no uno propio. */
  onExito: (mensaje: string) => void;
}) {
  const [parametros, setParametros] = useState<ParametroEditable[] | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cambiando, setCambiando] = useState<ParametroEditable | null>(null);
  const [historialDe, setHistorialDe] = useState<ParametroEditable | null>(null);
  const [versiones, setVersiones] = useState<VersionDeParametro[] | null>(null);
  const [falloDelHistorial, setFalloDelHistorial] = useState<Fallo | null>(null);

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const lista = await listarParametros();
      if (vivo.current) setParametros(lista ?? []);
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    }
  }, []);

  const verHistorial = useCallback(async (p: ParametroEditable) => {
    setHistorialDe(p);
    setVersiones(null);
    setFalloDelHistorial(null);
    try {
      const respuesta = await historialDeParametro(p.clave);
      const lista = (respuesta?.versiones ?? []) as VersionDeParametro[];
      // La más reciente primero.
      if (vivo.current) setVersiones([...lista].sort((a, b) => String(b.vigente_desde).localeCompare(String(a.vigente_desde))));
    } catch (e) {
      if (vivo.current) setFalloDelHistorial(aFallo(e));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const celda = (fila: FilaDeTabla, columna: ColumnaDeTabla): ReactNode => {
    const p = fila as unknown as ParametroEditable;
    switch (columna.key) {
      case 'etiqueta': return <span data-testid="reglas-parametro-etiqueta" className="font-medium">{p.etiqueta}</span>;
      case 'valor': return <span data-testid="reglas-parametro-valor"><Mono>{p.texto_del_valor}</Mono></span>;
      case 'estado': return p.estado ? <StatusBadge data-testid="reglas-parametro-estado">{p.estado}</StatusBadge> : null;
      case 'acciones':
        return (
          <span className="flex flex-wrap justify-end gap-1.5">
            <Button size="sm" variant="secondary" icon={Pencil} data-testid="reglas-parametro-cambiar"
              aria-label={`Cambiar ${p.etiqueta}`} onClick={() => setCambiando(p)}>Cambiar</Button>
            <Button size="sm" variant="ghost" icon={History} data-testid="reglas-parametro-ver-historial"
              aria-label={`Historial de ${p.etiqueta}`} onClick={() => { verHistorial(p); }}>Historial</Button>
          </span>
        );
      default: return null;
    }
  };

  return (
    <Card title="Parámetros del sistema" icon={SlidersHorizontal} pad={false}
      subtitle="Cada cambio aplica desde ese momento y queda en el historial con su motivo.">
      {fallo && (
        <div className="p-5 space-y-3">
          <Alert tone="danger" title="No se pudieron cargar los parámetros" data-testid="reglas-parametros-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { cargar(); }}>Reintentar</Button>
        </div>
      )}
      {!fallo && parametros === null && <p role="status" className="p-5 text-body text-ink-soft">Cargando parámetros…</p>}
      {!fallo && parametros && (
        <DataTable sinMarco columns={COLUMNAS} data-testid="reglas-parametros" testidDeFila="reglas-parametro-fila"
          rows={parametros.map((p) => ({ ...p, id: p.clave }))} renderCell={celda}
          empty={<EmptyState icon={SlidersHorizontal} data-testid="reglas-parametros-vacio" title="No hay parámetros editables" />} />
      )}

      {historialDe && (
        <div className="border-t border-hairline p-5" data-testid="reglas-parametro-historial">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-body font-semibold text-ink">Historial · {historialDe.etiqueta}</h3>
            <Button size="sm" variant="ghost" onClick={() => setHistorialDe(null)}>Cerrar el historial</Button>
          </div>
          {falloDelHistorial && <Alert tone="danger" title="No se pudo cargar el historial" detalleTecnico={falloDelHistorial.detalleTecnico}>{falloDelHistorial.message}</Alert>}
          {!falloDelHistorial && versiones === null && <p role="status" className="text-body text-ink-soft">Cargando el historial…</p>}
          {versiones && versiones.length === 0 && (
            <p data-testid="reglas-parametro-historial-vacio" className="text-body text-ink-soft">Todavía no tiene ningún valor fijado.</p>
          )}
          {versiones && versiones.length > 0 && (
            <ul className="divide-y divide-hairline">
              {versiones.map((v) => (
                <li key={v.version_id} data-testid="reglas-parametro-historial-fila" className="py-1.5 text-body">{v.texto}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ModalCambiarParametro parametro={cambiando} onCerrar={() => setCambiando(null)}
        onGuardado={(p) => {
          setCambiando(null);
          onExito(`${p.etiqueta} actualizado. Aplica desde ahora.`);
          cargar();
          if (historialDe?.clave === p.clave) verHistorial(p);
        }} />
    </Card>
  );
}
